import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ErrorResponse } from '../../custom-fetch';
import { Route } from '../../routes';
import { QueryKey } from '../../services/query-keys';
import { useUserService } from '../../services/users-service';
import IUser from '../../shared/IUser';
import ErrorsAlert from '../ErrorsAlert/ErrorsAlert';
import Spinner from '../ui/Spinner/Spinner';
import Table from '../ui/Table/Table';
import { useSortBy } from '../../hooks/useSortBy';

interface IProps {
  organizationId?: string;
}

enum EUserSortBy {
  name = "name",
  email = "email",
  username = "username",
  organizationName = "organizationName",
  role = "role",
  allowLogin = "allowLogin",
  allowGoogleLogin = "allowGoogleLogin",
  allowMicrosoftLogin = "allowMicrosoftLogin"
}

const UsersList: React.FC<IProps> = ({ organizationId }) => {
  const { fetchUsers } = useUserService();

  const navigate = useNavigate();
  const {
    data: users,
    isPending,
    isError,
    error,
  } = useQuery<IUser[], ErrorResponse>({
    queryKey: organizationId ? [QueryKey.users, 'organization', organizationId] : [QueryKey.users],
    queryFn: ({ signal }) => fetchUsers({ signal, organizationId }),
    staleTime: 5000
  });

  const sortFunction = (user: IUser, field: string) => {
    switch (field) {
      case EUserSortBy.name:
        return user.name.toLowerCase();
      case EUserSortBy.email:
        return user.email.toLowerCase();
      case EUserSortBy.username:
        return user.username?.toLowerCase() || '';
      case EUserSortBy.organizationName:
        return user.organizationName.toLowerCase();
      case EUserSortBy.role:
        return user.role.toLowerCase();
      case EUserSortBy.allowLogin:
        return user.allowLogin ? "true" : "false";
      case EUserSortBy.allowGoogleLogin:
        return user.allowGoogleLogin ? "true" : "false";
      case EUserSortBy.allowMicrosoftLogin:
        return user.allowMicrosoftLogin ? "true" : "false";
      default:
        return (user as any)[field]?.toString()?.toLowerCase() || "";
    }
  };

  const { sorted, sort, changeSortOrDirection, getChevron } = useSortBy(users, sortFunction);

  if (isError) {
    return <ErrorsAlert errors={error.messages} />;
  }

  if (isPending) {
    return <Spinner />;
  }

  if (!users || users.length === 0) {
    return <p>No users</p>;
  }

  const openHandler = (id: string) => {
    navigate(Route.user(id));
  }

  const headers = [
    { label: "Name", sortKey: EUserSortBy.name, isVisible: true },
    { label: "Email", sortKey: EUserSortBy.email, isVisible: true },
    { label: "Username", sortKey: EUserSortBy.username, isVisible: true },
    { label: "Organization", sortKey: EUserSortBy.organizationName, isVisible: !organizationId },
    { label: "Role", sortKey: EUserSortBy.role, isVisible: true },
    { label: "Password", sortKey: EUserSortBy.allowLogin, isVisible: true },
    { label: "Google", sortKey: EUserSortBy.allowGoogleLogin, isVisible: true },
    { label: "Microsoft", sortKey: EUserSortBy.allowMicrosoftLogin, isVisible: true },
  ];

  return (
    <Table hover>
      <thead>
        <tr>
          {headers.filter(h => h.isVisible).map(header => (
            <th 
              key={header.label} 
              onClick={() => changeSortOrDirection(header.sortKey)}
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {header.label}
                {sort === header.sortKey && getChevron()}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((user) => (
          <tr key={user.id} onClick={() => openHandler(user.id)}>
            <td>{user.name}</td>
            <td>{user.email}</td>
            <td>{user.username}</td>
            {!organizationId && <td>{user.organizationName}</td>}
            <td>{user.role}</td>
            <td style={{ textAlign: "center" }}>
              <FontAwesomeIcon 
                icon={user.allowLogin ? faCheck : faTimes} 
                style={{ color: user.allowLogin ? "green" : "red" }} 
              />
            </td>
            <td style={{ textAlign: "center" }}>
              <FontAwesomeIcon 
                icon={user.allowGoogleLogin ? faCheck : faTimes} 
                style={{ color: user.allowGoogleLogin ? "green" : "red" }} 
              />
            </td>
            <td style={{ textAlign: "center" }}>
              <FontAwesomeIcon 
                icon={user.allowMicrosoftLogin ? faCheck : faTimes} 
                style={{ color: user.allowMicrosoftLogin ? "green" : "red" }} 
              />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default UsersList;
